import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useHub } from "../lib/hubContent";
import { EditSectionButton } from "./HubEditor";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";

const HEADER_CLASS = { prismatic: "bg-gradient-to-br from-[#EADEC9] via-[#D2C0A5] to-[#5A6B4C]" };
const CHIP_CLASS = { prismatic: "bg-amber-100 text-amber-800" };

export default function BrandPortals() {
  const c = useHub().section("brands");
  return (
    <section id="brands" data-testid="brands-section" className="relative bg-[#F6EEFA] text-[#45215C] py-24 md:py-32">
      <EditSectionButton sectionKey="brands" label="Edit brands" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow={c.eyebrow}
          title={<>Built from <span className="italic text-en-purple">here.</span></>}
          note={c.note}
          noteColor="text-en-orange"
        />

        <div className="max-w-2xl mx-auto">
          {(c.brands || []).map((b, i) => (
            <Reveal key={b.id} delay={i * 0.12}>
              <article
                data-testid={`brand-portal-${b.id}`}
                className="group rounded-3xl overflow-hidden bg-white border border-purple-200/70 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 h-full flex flex-col"
              >
                <div className={`relative h-48 ${HEADER_CLASS[b.id] || "bg-gradient-to-br from-stone-200 via-stone-300 to-stone-500"} flex items-end p-7 overflow-hidden`}>
                  <span className="absolute -top-6 -right-4 font-display font-black text-[7rem] leading-none text-white/15 select-none" aria-hidden="true">
                    {b.name.charAt(0)}
                  </span>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/80 mb-2">{b.est}</p>
                    <h3 className="font-display font-black text-3xl md:text-4xl text-white leading-none">{b.name}</h3>
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-purple-900/60">{b.tag}</p>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[9px] tracking-[0.15em] uppercase ${CHIP_CLASS[b.id] || "bg-stone-100 text-stone-700"}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      {b.status}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-purple-950/70 flex-1">{b.desc}</p>
                  {b.id === "prismatic" ? (
                    <Link
                      to="/prismatic-essence"
                      data-testid={`brand-portal-visit-${b.id}`}
                      className="mt-7 inline-flex items-center gap-2 self-start rounded-full px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-white transition-transform group-hover:scale-105"
                      style={{ backgroundColor: b.accent }}
                    >
                      Visit the portal
                      <ArrowUpRight size={14} />
                    </Link>
                  ) : (
                    <button
                      data-testid={`brand-portal-visit-${b.id}`}
                      onClick={() =>
                        toast(`${b.name} opens soon.`, {
                          description: "You're early — that's kind of the point.",
                        })
                      }
                      className="mt-7 inline-flex items-center gap-2 self-start rounded-full px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-white transition-transform group-hover:scale-105"
                      style={{ backgroundColor: b.accent }}
                    >
                      Visit the portal
                      <ArrowUpRight size={14} />
                    </button>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
